import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthService } from '../services/api/auth/AuthService';
import { EmpresaStatusService } from '../services/api/cadastros/EmpresaStatusService';

interface IAuthContextData {
  logout: () => void;
  isAuthenticated: boolean;
  isSistema: boolean;
  // true quando o login foi válido mas a empresa está desativada no painel de controle —
  // o Login.tsx usa isso pra mostrar a tela de suporte em vez de entrar no sistema.
  empresaInativa: boolean;
  login: (login: string, senha: string) => Promise<string | void>;
  sistema: (sistemas: string)  => void;
  logoutsistema: () => void;
}
const AuthContext = createContext({} as IAuthContextData);
const LOCAL_STORAGE_KEY__ACCESS_TOKEN = 'APP_ACCESS_TOKEN';
interface IAuthProviderProps {
  children: React.ReactNode;
}
export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>();
  const [accessSistema, setAccessSistema] = useState<string>();
  const [empresaInativa, setEmpresaInativa] = useState(false);

  // Confere se a empresa continua ativa e, se não estiver, derruba a sessão — chamado
  // tanto num login novo quanto ao restaurar uma sessão salva (useEffect abaixo), pra uma
  // empresa desativada enquanto alguém já estava logado também ser barrada no próximo
  // carregamento da página, não só em quem loga de novo.
  const verificarEmpresaAtiva = useCallback(async () => {
    const ativa = await EmpresaStatusService.verificarAtiva();
    if (!ativa) {
      localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
      setAccessToken(undefined);
      setEmpresaInativa(true);
    }
    return ativa;
  }, []);

  useEffect(() => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);

    if (!accessToken) {
      setAccessToken(undefined);
      return;
    }

    // Mesma defesa já usada em axiosApi/axiosLogin (getStoredToken): um valor
    // corrompido/não-JSON aqui (ex.: sobra de uma versão antiga que gravava o
    // token cru, sem JSON.stringify) não pode derrubar o app inteiro logo no
    // mount — trata como deslogado e limpa o lixo.
    try {
      const token = JSON.parse(accessToken);
      setAccessToken(token);
      // Precisa do token já no state (e no localStorage, de onde o interceptor do Api
      // lê) antes dessa chamada, senão ela sai sem Authorization.
      verificarEmpresaAtiva();
    } catch {
      localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
      setAccessToken(undefined);
    }
  }, [verificarEmpresaAtiva]);
  const handleLogin = useCallback(async (login: string, senha: string) => {
    const result = await AuthService.auth(login, senha);

    if (result instanceof Error) {
      throw new Error(result.message);
    } else {
      // Salva no localStorage antes de setar accessToken de propósito: o interceptor de
      // request lê o token de lá, e a checagem de empresa ativa logo abaixo precisa dele —
      // mas só marca isAuthenticated (via setAccessToken) depois de confirmar a empresa,
      // pra não deixar o sistema aparecer nem por um instante pra empresa desativada.
      localStorage.setItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN, JSON.stringify(result.accessToken));
      setEmpresaInativa(false);

      const ativa = await EmpresaStatusService.verificarAtiva();
      if (!ativa) {
        localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
        setEmpresaInativa(true);
        return;
      }

      setAccessToken(result.accessToken);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
    setAccessToken(undefined);
    setAccessSistema('');
    setEmpresaInativa(false);
  }, []);

  const handleLogoutsistema = useCallback(() => {
    setAccessSistema('');
  }, []);

  const handleSistema = useCallback((sistemas: string) => {
    setAccessSistema(sistemas);
  }, []);
  const isAuthenticated = useMemo(() => !!accessToken, [accessToken]);
  const isSistema = useMemo(() => !!accessSistema, [accessSistema]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isSistema, empresaInativa, sistema: handleSistema,  logoutsistema: handleLogoutsistema,  login: handleLogin, logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
};
export const useAuthContext = () => useContext(AuthContext);